import React, { createContext, useContext } from "react";
import Swal from "sweetalert2";

const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
export const resolveSignatureSrc = (v) => {
  if (!v) return null;
  return v.startsWith("data:") ? v : `${STORAGE_URL}${v}`;
};

// Context supplies form state to hoisted helper components so their function
// identity stays stable across renders (prevents inputs losing focus).
const LeapFormCtx = createContext(null);

const Bullet = ({ children }) => <li className="mb-1">{children}</li>;

const SignRow = ({ label, nameField, sigField, dateField }) => {
  const { formData, setFormData, readOnly } = useContext(LeapFormCtx);
  const src = resolveSignatureSrc(formData[sigField]);

  const onUpload = (file) => {
    if (readOnly || !file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire({ title: "Invalid file", text: "Please upload an image file.", icon: "warning" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFormData((prev) => ({ ...prev, [sigField]: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="row g-3 align-items-center border-bottom py-3">
      <div className="col-md-4">
        <div className="form-floating form-floating-outline">
          <input type="text" className="form-control" value={formData[nameField] || ""} onChange={(e) => setFormData((prev) => ({ ...prev, [nameField]: e.target.value }))} disabled={readOnly} />
          <label>{label} Name</label>
        </div>
      </div>
      <div className="col-md-4">
        {src ? (
          <div className="d-flex align-items-center gap-2">
            <img src={src} alt={`${label} signature`} style={{ height: 55, maxWidth: 170, objectFit: "contain", border: "1px solid #dee2e6", borderRadius: 4, background: "#fff" }} />
            {!readOnly && (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFormData((prev) => ({ ...prev, [sigField]: "" }))}>
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        ) : (
          <input type="file" accept="image/*" className="form-control form-control-sm" onChange={(e) => onUpload(e.target.files[0])} disabled={readOnly} />
        )}
      </div>
      <div className="col-md-4">
        <div className="form-floating form-floating-outline">
          <input type="date" className="form-control" value={formData[dateField] || ""} onChange={(e) => setFormData((prev) => ({ ...prev, [dateField]: e.target.value }))} disabled={readOnly} />
          <label>Date</label>
        </div>
      </div>
    </div>
  );
};

/**
 * LEAP Participation Consent Form
 * Lived Experience Advisory Panel (LEAP) — Caravan of Life Trust Pakistan (CoLPT)
 */
const LeapParticipationConsentForm = ({ formData, setFormData, readOnly = false }) => {
  const toggle = (name) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <LeapFormCtx.Provider value={{ formData, setFormData, readOnly }}>
      <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
        <div className="text-center mb-3">
          <h5 className="fw-bold mb-1">LEAP PARTICIPATION CONSENT FORM</h5>
          <p className="text-muted small mb-0">Lived Experience Advisory Panel (LEAP) — Caravan of Life Trust Pakistan (CoLPT)</p>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Purpose</small>
          <p className="small mb-0">
            LEAP is an advisory panel of individuals with lived experience who provide feedback and guidance to improve CoLPT's
            mental health services, programs, and policies.
          </p>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Voluntary Participation</small>
          <p className="small mb-1">I understand that:</p>
          <ul className="small mb-0">
            <Bullet>My participation is voluntary.</Bullet>
            <Bullet>I may withdraw at any time without penalty.</Bullet>
            <Bullet>My services at CoLPT (including TRH) will not be affected if I choose not to participate or withdraw.</Bullet>
          </ul>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Responsibilities</small>
          <p className="small mb-1">As a LEAP member, I agree to:</p>
          <ul className="small mb-1">
            <Bullet>Attend meetings regularly and inform staff if I cannot attend.</Bullet>
            <Bullet>Participate respectfully and constructively.</Bullet>
            <Bullet>Maintain confidentiality of discussions and members.</Bullet>
            <Bullet>Follow ethical guidelines and declare any conflicts of interest.</Bullet>
          </ul>
          <p className="small mb-0 fst-italic">I understand that continued participation depends on responsible and respectful involvement.</p>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Use of My Contributions</small>
          <ul className="small mb-0">
            <Bullet>My insights and feedback may be used to improve services, programs, research, and policies.</Bullet>
            <Bullet>My lived experience may be shared in reports or presentations in a non-identifiable way.</Bullet>
            <Bullet>My name or personal story will not be used publicly without my written permission.</Bullet>
          </ul>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Data Protection</small>
          <ul className="small mb-0">
            <Bullet>My personal information will be kept confidential and stored securely by CoLPT.</Bullet>
            <Bullet>I will not have access to confidential client records unless officially authorized.</Bullet>
            <Bullet>Shared materials are for advisory purposes only and will not be distributed without permission.</Bullet>
          </ul>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Emotional Safety</small>
          <p className="small mb-0">
            I understand that discussions may be sensitive. I may pause, step out, or decline to share at any time.
          </p>
        </div>

        <div className="mb-3">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Agreement</small>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="chk_agreement_confirmed" checked={!!formData.agreement_confirmed} onChange={() => toggle("agreement_confirmed")} disabled={readOnly} />
            <label className="form-check-label" htmlFor="chk_agreement_confirmed">
              By signing below, I confirm that I understand my role and agree to participate in LEAP.
            </label>
          </div>
        </div>

        <div className="mb-2">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Signatures</small>
          <SignRow label="Participant" nameField="participant_name" sigField="participant_signature" dateField="participant_sign_date" />
          <SignRow label="Staff" nameField="staff_name" sigField="staff_signature" dateField="staff_sign_date" />
        </div>
      </div>
    </LeapFormCtx.Provider>
  );
};

export default LeapParticipationConsentForm;
